
import { useState, useEffect } from "react";
import { X, Loader2, Info } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { useI18n } from "@/lib/i18n";
import { ImageCaptureInput } from "@/components/ImageCaptureInput";
import { ImageCropperModal } from "./ImageCropperModal";
import { DialogDescription } from "@/components/ui/dialog";

export interface WishlistItem {
    id: string;
    item_name: string;
    image_url?: string | null;
    image_thumbnail_url?: string | null;
    dimensions?: string | null;
    guide_price?: number | null;
    notes?: string | null;
    is_found: boolean;
    found_post_id: string | null;
    is_private: boolean;
    created_at: string;
    found_at: string | null;
}

type UploadImageFn = {
    (base64: string): Promise<{ url: string; thumbnail: string }>;
};

interface FormDialogProps {
    open: boolean;
    onClose: () => void;
    item?: WishlistItem | null;
    initialData?: {
        item_name?: string;
        image_url?: string;
        image_thumbnail_url?: string;
        notes?: string;
    };
    onSubmit: (data: { item_name: string; image_url?: string; image_thumbnail_url?: string; dimensions?: string; guide_price?: string; notes?: string; is_private?: boolean }) => void;
    isLoading: boolean;
    uploadImage: UploadImageFn;
}

export function WishlistItemFormDialog({ open, onClose, item, initialData, onSubmit, isLoading, uploadImage }: FormDialogProps) {
    const { t } = useI18n();
    const draftStorageKey = "wishlist_item_draft_v1";
    const isEditMode = !!item;
    const [name, setName] = useState(item?.item_name || initialData?.item_name || "");
    const [dimensions, setDimensions] = useState(item?.dimensions || "");
    const [guidePrice, setGuidePrice] = useState(item?.guide_price ? String(item.guide_price) : "");
    const [notes, setNotes] = useState(item?.notes || initialData?.notes || "");
    const [imageUrl, setImageUrl] = useState(item?.image_url || initialData?.image_url || "");
    const [thumbUrl, setThumbUrl] = useState(item?.image_thumbnail_url || initialData?.image_thumbnail_url || "");
    const [isPrivate, setIsPrivate] = useState(item?.is_private || false);
    const [uploading, setUploading] = useState(false);
    const [pendingImage, setPendingImage] = useState<string | null>(null);

    // Reset form when item or initialData changes
    useEffect(() => {
        if (open) {
            if (item) {
                setName(item.item_name || "");
                setDimensions(item.dimensions || "");
                setGuidePrice(item.guide_price ? String(item.guide_price) : "");
                setNotes(item.notes || "");
                setImageUrl(item.image_url || "");
                setThumbUrl(item.image_thumbnail_url || "");
                setIsPrivate(item.is_private || false);
                return;
            }

            const baseData = {
                item_name: initialData?.item_name || "",
                dimensions: "",
                guide_price: "",
                notes: initialData?.notes || "",
                image_url: initialData?.image_url || "",
                image_thumbnail_url: initialData?.image_thumbnail_url || "",
                is_private: false,
            };

            if (typeof window !== "undefined") {
                try {
                    const rawDraft = window.localStorage.getItem(draftStorageKey);
                    if (rawDraft) {
                        const draft = JSON.parse(rawDraft) as Partial<{
                            item_name: string;
                            dimensions: string;
                            guide_price: string;
                            notes: string;
                            image_url: string;
                            image_thumbnail_url: string;
                            is_private: boolean;
                        }>;
                        setName(draft.item_name ?? baseData.item_name);
                        setDimensions(draft.dimensions ?? baseData.dimensions);
                        setGuidePrice(draft.guide_price ?? baseData.guide_price);
                        setNotes(draft.notes ?? baseData.notes);
                        setImageUrl(draft.image_url ?? baseData.image_url);
                        setThumbUrl(draft.image_thumbnail_url ?? baseData.image_thumbnail_url);
                        setIsPrivate(draft.is_private ?? baseData.is_private);
                        return;
                    }
                } catch {
                    // Ignore malformed local draft and fall back to initial data.
                }
            }

            setName(baseData.item_name);
            setDimensions(baseData.dimensions);
            setGuidePrice(baseData.guide_price);
            setNotes(baseData.notes);
            setImageUrl(baseData.image_url);
            setThumbUrl(baseData.image_thumbnail_url);
            setIsPrivate(baseData.is_private);
        }
    }, [item?.id, initialData, open]);

    useEffect(() => {
        if (!open || isEditMode) return;
        if (typeof window === "undefined") return;

        const draft = {
            item_name: name,
            dimensions,
            guide_price: guidePrice,
            notes,
            image_url: imageUrl,
            image_thumbnail_url: thumbUrl,
            is_private: isPrivate,
        };

        try {
            window.localStorage.setItem(draftStorageKey, JSON.stringify(draft));
        } catch {
            // Best-effort draft persistence.
        }
    }, [open, isEditMode, name, dimensions, guidePrice, notes, imageUrl, thumbUrl, isPrivate]);

    const handleImageSelected = async (base64: string) => {
        setPendingImage(base64);
    };

    const handleCrop = async (croppedBase64: string) => {
        setUploading(true);
        setPendingImage(null);
        try {
            const result = await uploadImage(croppedBase64);
            setImageUrl(result.url);
            setThumbUrl(result.thumbnail);
        } catch {
            console.error("Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = () => {
        if (!name.trim()) return;
        if (!isEditMode && typeof window !== "undefined") {
            try {
                window.localStorage.removeItem(draftStorageKey);
            } catch {
                // Ignore storage cleanup errors.
            }
        }
        onSubmit({
            item_name: name.trim(),
            image_url: imageUrl || undefined,
            image_thumbnail_url: thumbUrl || undefined,
            dimensions: dimensions.trim() || undefined,
            guide_price: guidePrice || undefined,
            notes: notes.trim() || undefined,
            is_private: isPrivate,
        });
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{item ? t("wishlist.editItem") : t("wishlist.addItem")}</DialogTitle>
                    <DialogDescription className="sr-only">
                        {item ? "Pas item aan in je wishlist" : "Voeg een nieuw item toe aan je wishlist"}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label>{t("wishlist.itemName") || "Item naam"}</Label>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t("itemWishlist.placeholder") || "Bijv. Vintage lamp..."}
                            autoFocus
                        />
                    </div>

                    <div>
                        <Label>{t("wishlist.imageOptional") || "Foto (optioneel)"}</Label>
                        {imageUrl ? (
                            <div className="relative mt-1">
                                <img src={thumbUrl || imageUrl} alt="" className="w-full aspect-square object-cover rounded-lg border" crossOrigin="anonymous" />
                                <Button variant="destructive" size="icon" className="absolute top-2 right-2 h-7 w-7" onClick={() => { setImageUrl(""); setThumbUrl(""); }}>
                                    <X className="h-3 w-3" />
                                </Button>
                            </div>
                        ) : (
                            <div className="mt-1">
                                <ImageCaptureInput
                                    onImageSelected={handleImageSelected}
                                    label={uploading ? "..." : t("wishlist.imageOptional") || "Maak een foto of kies bestand"}
                                    className="w-full justify-center text-muted-foreground hover:text-primary"
                                />
                            </div>
                        )}
                    </div>

                    <ImageCropperModal
                        open={!!pendingImage}
                        onOpenChange={(v) => { if (!v) setPendingImage(null); }}
                        image={pendingImage || ""}
                        onCrop={handleCrop}
                    />

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>{t("wishlist.dimensions") || "Afmetingen"}</Label>
                            <Input
                                value={dimensions}
                                onChange={(e) => setDimensions(e.target.value)}
                                placeholder={t("wishlist.dimensionsPlaceholder") || "Bijv. 50x30cm"}
                            />
                        </div>
                        <div>
                            <Label>{t("wishlist.guidePrice") || "Richtprijs"} (€)</Label>
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={guidePrice}
                                onChange={(e) => setGuidePrice(e.target.value)}
                                placeholder={t("wishlist.guidePricePlaceholder") || "0.00"}
                            />
                        </div>
                    </div>

                    <div>
                        <Label>{t("wishlist.notes") || "Notities"}</Label>
                        <Textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder={t("wishlist.notesPlaceholder") || "Extra details..."}
                            rows={2}
                        />
                    </div>

                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border/50">
                        <div className="flex items-center gap-2">
                            <Label htmlFor="item-private" className="font-bold cursor-pointer">
                                {t("wishlist.private")}
                            </Label>
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button type="button" className="text-muted-foreground hover:text-primary transition-colors">
                                            <Info className="h-4 w-4" />
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom" className="max-w-[200px] text-xs">
                                        {t("wishlist.privateInfo")}
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                        <Switch
                            id="item-private"
                            checked={isPrivate}
                            onCheckedChange={setIsPrivate}
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
                    <Button onClick={handleSubmit} disabled={!name.trim() || isLoading || uploading}>
                        {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : item ? t("wishlist.saveChanges") || "Wijzigingen opslaan" : t("wishlist.addItem") || "Toevoegen aan wishlist"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
